import { SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAvailability, useSetAvailability, useValuesByAxisCode, useValueWithAxis } from '@/hooks/useClassification'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { AxisValueDTO } from '@/services/classification.service'

/** The sentinel standing for a null `geoValueId` — the worldwide default row. */
const WORLDWIDE = '__worldwide__'

/**
 * A geography value with how deep it sits, so the list reads as the tree it is.
 *
 * The registry serves values parent-before-child, so accumulating depth in one pass matches the
 * order they arrive in — no sort, and no assumption that ids ascend with depth.
 */
function withDepth(geoValues: AxisValueDTO[]): { value: AxisValueDTO; depth: number }[] {
	const byId = new Map(geoValues.map((v) => [v.valueId, v]))
	const depthOf = (v: AxisValueDTO): number => {
		let depth = 0
		let cursor: AxisValueDTO | undefined = v
		// Guarded by the map lookup: a parent outside this axis (or a cycle) stops the walk rather
		// than spinning, so a malformed tree costs indentation, not the page.
		while (cursor?.parentId && byId.has(cursor.parentId) && depth < geoValues.length) {
			depth++
			cursor = byId.get(cursor.parentId)
		}
		return depth
	}
	return geoValues.map((value) => ({ value, depth: depthOf(value) }))
}

/**
 * Which markets a value is valid in — the availability editor.
 *
 * THE STATE THAT IS NOT A CHECKBOX. Availability has three states, and the difference between two
 * of them is the whole reason this table exists:
 *
 *   - no rows at all      → UNDESCRIBED. Nobody has said where this value is sold, and every read
 *                           leaves it alone rather than claiming it is everywhere.
 *   - a worldwide row     → available EVERYWHERE, as a recorded claim.
 *   - specific rows       → available in those scopes, and only those.
 *
 * So "nothing checked" is not the same as "worldwide checked", and the page says so in words
 * rather than relying on the reader to infer it from empty boxes.
 *
 * Resolution is closure-aware and downward: checking a REGION covers every market inside it, so a
 * fact known only at region level is recorded at region level rather than enumerated.
 */
/**
 * The availability editor for one value, without a page around it.
 *
 * Extracted so the value-detail tab and the standalone page are the SAME editor: the
 * three-state rule below is subtle enough that two copies would drift, and the difference
 * between "undescribed" and "worldwide" is the whole reason this exists.
 */
export function AvailabilityEditor({ valueId }: { valueId: string }) {
	const { data: availability = [], isLoading } = useAvailability(valueId)
	const { data: geoValues = [] } = useValuesByAxisCode('geography')
	const setAvailability = useSetAvailability(valueId)

	// The server's set, as the checkbox keys this page uses.
	const saved = useMemo(() => new Set(availability.map((row) => row.geoValueId ?? WORLDWIDE)), [availability])
	const [selected, setSelected] = useState<Set<string>>(saved)

	// Re-sync when the read lands or changes underneath — otherwise the editor would show the
	// empty initial set forever, and a save would wipe the value's real availability.
	useEffect(() => setSelected(saved), [saved])

	const rows = useMemo(() => withDepth(geoValues), [geoValues])
	const isWorldwide = selected.has(WORLDWIDE)
	const dirty = selected.size !== saved.size || [...selected].some((key) => !saved.has(key))

	const toggle = (key: string) =>
		setSelected((current) => {
			const next = new Set(current)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})

	const save = () => {
		// `null` for the worldwide row: the API takes geography ids, and the default is the absence
		// of a scope rather than a scope of its own.
		const geoValueIds = [...selected].map((key) => (key === WORLDWIDE ? null : key)) as string[]
		setAvailability.mutate(geoValueIds, {
			onSuccess: () => notification.success('Availability saved.'),
			onError: (error) => notification.error(apiErrorMessage(error, 'Could not save availability.')),
		})
	}

	const summary = isLoading
		? 'Loading…'
		: availability.length === 0
			? 'Not described — treated as available everywhere until a market is chosen.'
			: isWorldwide
				? 'Available everywhere (worldwide default).'
				: `Restricted to ${availability.length} scope${availability.length === 1 ? '' : 's'}.`

	return (
		<div className="space-y-3">
			<p className="text-ds-text-muted text-sm">{summary}</p>
			<SectionCard
				title="Where this value is valid"
				description="Checking a region covers every market inside it. Leaving everything unchecked is not “nowhere” — it means nobody has described this value, and reads treat it as unrestricted."
			>
				<div className="space-y-4">
					<label className="flex items-center gap-2 text-sm font-medium">
						<input type="checkbox" checked={isWorldwide} onChange={() => toggle(WORLDWIDE)} />
						Worldwide default — available everywhere
					</label>

					{/* Still listed while worldwide is on: a specific row is not contradicted by the default,
					    it simply is not what decides the answer, and hiding them would lose work the moment
					    someone toggled worldwide on and off. */}
					<ul className="space-y-1">
						{rows.map(({ value, depth }) => (
							<li key={value.valueId} style={{ paddingLeft: `${depth * 1.25}rem` }}>
								<label className="flex items-center gap-2 text-sm">
									<input type="checkbox" checked={selected.has(value.valueId)} onChange={() => toggle(value.valueId)} />
									<span className={isWorldwide ? 'text-ds-text-subtle' : undefined}>{value.label}</span>
								</label>
							</li>
						))}
					</ul>

					<div className="flex items-center gap-3 pt-2">
						<Button onPress={save} isDisabled={!dirty || setAvailability.isPending}>
							{setAvailability.isPending ? 'Saving…' : 'Save availability'}
						</Button>
						{dirty ? <span className="text-sm text-ds-text-muted">Unsaved changes</span> : null}
					</div>
				</div>
			</SectionCard>
		</div>
	)
}

/**
 * The standalone availability page, kept so an existing link still resolves. The value
 * detail page is where this now lives.
 */
export function AvailabilityPage() {
	const { valueId = '' } = useParams()
	const subject = useValueWithAxis(valueId)

	return (
		<div className="space-y-6">
			<div>
				<Link to="/admin/classification" className="text-ds-info text-sm hover:underline">
					← Axis registry
				</Link>
				<h1 className="mt-1 font-semibold text-2xl">{subject ? subject.value.label : `Value #${valueId}`}</h1>
				<p className="text-ds-text-muted text-sm">
					Market availability{subject?.axis ? ` · ${subject.axis.name}` : ''}
					{subject ? ` · ${subject.value.code}` : ''}
				</p>
			</div>
			<AvailabilityEditor valueId={valueId} />
		</div>
	)
}
